import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { AdminOptionsNav } from '../admin-options-nav';
import {
  AdminToolId,
  getActiveAdminTool,
  getAdminSettingsToolLink,
  getAdminToolsGroupedByCategory,
  isAdminToolRouteActive,
} from './molecules/admin-tool-registry/admin-tool.registry';
import {
  ADMIN_SETTINGS_VIEW_SUBMISSION_URL_PARAM,
  AdminSettingsContextService,
} from './services/admin-settings-context.service';

@Component({
  selector: 'admin-settings',
  imports: [AdminOptionsNav, RouterLink, RouterOutlet],
  templateUrl: './admin-settings.html',
  styleUrl: './admin-settings.scss',
  host: { class: 'block h-full w-full min-w-0' },
  providers: [AdminSettingsContextService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminSettings {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly settingsContext = inject(AdminSettingsContextService);
  readonly toolGroups = getAdminToolsGroupedByCategory();
  readonly viewSubmissionUrlInput = signal('');
  readonly viewSubmissionUrlError = signal(false);
  readonly contextExpanded = signal(false);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects.split('?')[0] ?? ''),
      startWith(this.router.url.split('?')[0] ?? ''),
    ),
    { initialValue: this.router.url.split('?')[0] ?? '' },
  );

  constructor() {
    this.applyViewSubmissionUrlFromQuery();
  }

  readonly activeTool = computed(() => getActiveAdminTool(this.currentUrl()));

  readonly showContextBar = computed(() => {
    const context = this.activeTool()?.context;
    return Boolean(context?.briefId || context?.creatorId);
  });

  readonly showBriefIdField = computed(() => this.activeTool()?.context.briefId ?? false);

  readonly showCreatorIdField = computed(() => this.activeTool()?.context.creatorId ?? false);

  readonly contextHint = computed<'needs-brief' | 'needs-creator' | null>(() => {
    if (this.showBriefIdField() && this.settingsContext.needsBriefId()) {
      return 'needs-brief';
    }
    if (this.showCreatorIdField() && this.settingsContext.needsCreatorId()) {
      return 'needs-creator';
    }
    return null;
  });

  getToolLink(toolId: AdminToolId): string {
    return getAdminSettingsToolLink(toolId);
  }

  isToolSelected(toolId: AdminToolId): boolean {
    return isAdminToolRouteActive(toolId, this.currentUrl());
  }

  getToolNavClass(toolId: AdminToolId): string {
    return this.isToolSelected(toolId)
      ? 'border-primary/50 bg-primary/10 ring-1 ring-primary/35 dark:border-primary/45 dark:bg-primary/15 dark:ring-primary/25'
      : 'border-transparent hover:border-zinc-300 hover:bg-zinc-50 dark:hover:border-zinc-700 dark:hover:bg-zinc-900/50';
  }

  onContextBriefIdInput(value: string): void {
    this.settingsContext.setBriefId(value);
  }

  onContextCreatorIdInput(value: string): void {
    this.settingsContext.setCreatorId(value);
  }

  toggleContextExpanded(): void {
    this.contextExpanded.update((expanded) => !expanded);
  }

  onViewSubmissionUrlInput(value: string): void {
    this.viewSubmissionUrlInput.set(value);
    this.viewSubmissionUrlError.set(false);

    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }

    const applied = this.settingsContext.applyViewSubmissionUrl(trimmed);
    this.viewSubmissionUrlError.set(!applied);
  }

  clearContext(): void {
    this.settingsContext.clear();
    this.viewSubmissionUrlInput.set('');
    this.viewSubmissionUrlError.set(false);
  }

  private applyViewSubmissionUrlFromQuery(): void {
    const fromQuery =
      this.router.parseUrl(this.router.url).queryParamMap.get(ADMIN_SETTINGS_VIEW_SUBMISSION_URL_PARAM)?.trim() ||
      this.route.snapshot.queryParamMap.get(ADMIN_SETTINGS_VIEW_SUBMISSION_URL_PARAM)?.trim();

    if (!fromQuery) {
      return;
    }

    this.onViewSubmissionUrlInput(fromQuery);
    this.contextExpanded.set(true);

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [ADMIN_SETTINGS_VIEW_SUBMISSION_URL_PARAM]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
